// A new customer as the office types one in (QA-323): from a call, from Book's customer field or
// from Customers. The form is plain text; this turns it into customers.create's input, or says
// what to fix first.

import { normalizePhone } from '@flowology/kernel';

export interface CustomerForm {
  name: string;
  /** Their mobile, as typed. A call's number is given separately and isn't typed. */
  mobile: string;
  /** OK to text about their appointments and invoices (asked, then saved with who and when). */
  sms: boolean;
  email: string;
  mail: boolean;
  line1: string;
  city: string;
  kind: 'residential' | 'commercial';
}

export const emptyCustomerForm = (sms = false): CustomerForm => ({
  name: '',
  mobile: '',
  sms,
  email: '',
  mail: false,
  line1: '',
  city: '',
  kind: 'residential',
});

/**
 * A search that found no one starts the form with what was typed: a phone number as the mobile, a
 * street ("12 Oak St") as the service address, anything else as the name.
 */
export function formFromSearch(query: string): CustomerForm {
  const q = query.trim();
  const f = emptyCustomerForm();
  if (!q) return f;
  if (normalizePhone(q)) return { ...f, mobile: q };
  if (/^\d+[a-z]?\s+\S/i.test(q)) return { ...f, line1: q };
  return { ...f, name: q };
}

/**
 * customers.create's input, or the one thing to fix. `phone` is a call's number (already E.164);
 * otherwise the typed mobile is used, normalised the way the server does it.
 */
export function newCustomerInput(
  f: CustomerForm,
  opts: { phone?: string; country: string },
): { input: Record<string, unknown> } | { error: string } {
  const name = f.name.trim();
  if (!name) return { error: 'Add their name.' };
  const phone = opts.phone ?? (f.mobile.trim() ? normalizePhone(f.mobile) : undefined);
  if (phone === null)
    return { error: 'That mobile number doesn’t look right. Type it with its area code, like 604-555-0198.' };
  const line1 = f.line1.trim();
  const city = f.city.trim();
  if (line1 && !city) return { error: 'Add the city for the service address.' };
  if (city && !line1) return { error: 'Add the street for the service address.' };
  const email = f.email.trim();
  return {
    input: {
      kind: f.kind,
      displayName: name,
      contacts: [
        {
          name,
          phones: phone ? [{ value: phone, smsConsent: f.sms, consentSource: 'verbal' }] : [],
          emails: email ? [{ value: email, emailConsent: f.mail, consentSource: 'verbal' }] : [],
        },
      ],
      locations: line1 ? [{ address: { line1, city, country: opts.country } }] : [],
    },
  };
}

/** What a possible duplicate shares (customers.possibleDuplicates, BL-029), as the notice says it. */
export type Shared = 'phone' | 'email' | 'address';

/** "mobile", "phone number and address", "mobile, email and address": a call's number is a phone number. */
export function dupeReason(same: readonly Shared[], fromCall: boolean): string {
  const words = same.map((s) => (s === 'phone' ? (fromCall ? 'phone number' : 'mobile') : s));
  return words.length > 1 ? `${words.slice(0, -1).join(', ')} and ${words.at(-1)}` : (words[0] ?? '');
}

/**
 * What the duplicate check asks with (customers.possibleDuplicates): the number (the call's, or the
 * mobile typed), the email, and the street with its city. Null when nothing is typed to match on.
 */
export function dupeQuery(
  f: CustomerForm,
  phone?: string,
): { phone?: string; email?: string; line1?: string; city?: string } | null {
  const mobile = phone ?? f.mobile.trim();
  const q = {
    ...(mobile ? { phone: mobile } : {}),
    ...(f.email.trim() ? { email: f.email.trim() } : {}),
    ...(f.line1.trim() && f.city.trim() ? { line1: f.line1.trim(), city: f.city.trim() } : {}),
  };
  return Object.keys(q).length ? q : null;
}

/**
 * Whether saving checks for a possible duplicate first (QA-699): not after "Add anyway", and not on
 * "Try again" after a lost answer (QA-735, 25 Oct). That try reached customers.create with this same
 * key, which answers it with the customer it already made; the check would only find them and hold
 * the retry as a "duplicate". Changed details are a new key, so they are checked again.
 */
export function checksFirst(o: { anyway: boolean; key: string; sentKey: string | null }): boolean {
  return !o.anyway && o.key !== o.sentKey;
}

/**
 * The warning before adding a customer who may already be one (QA-699): who, and what they share, as
 * the office's search shows them. Saving stops on it until "Add anyway" (a tenant or a new owner can
 * share them, F-41).
 */
export function dupeNotice(
  m: { displayName: string; same: readonly Shared[] },
  o: { fromCall: boolean; who: string; held: boolean; pick: 'Use' | 'Open' },
): string {
  const line = `Possible duplicate: ${m.displayName} (same ${dupeReason(m.same, o.fromCall)}).`;
  return o.held
    ? `${line} Not added yet: ${o.pick.toLowerCase()} them, or add ${o.who} anyway if they really are different (a tenant, say).`
    : `${line} ${o.pick} them, or add ${o.who} as a new customer if they really are different (a tenant, say).`;
}
